import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import keyBy from 'lodash.keyby';

import { getArchivedWallet } from 'common/api/wallet/cards';
import { useApi } from 'common/modules/async-actions/useApi';
import { upsertBrands } from 'common/features/store/duck/brands/duck';
import { ECommBrand } from 'common/api/e-comm/models/ECommBrand';
import { brandLoader } from 'common/api/wallet/cards/utils/brandLoader';
import { Card } from 'common/api/e-comm/models/Card';

export const useArchivedCards = (setHasFetchedCards: (_: boolean) => void) => {
    const [loadArchivedCards, state, result] = useApi(getArchivedWallet);
    const archivedCards: Card[] = useMemo(
        () => (result && result.data ? result.data : []),
        [result]
    );
    const isLoading = state.loading;
    const dispatch = useDispatch();

    // Side-Effect [A].
    // Load the suer's archived cards
    useEffect(() => {
        (async () => {
            const response = await loadArchivedCards();

            // CASE: user has no archived cards we setHasFetchedcards to true
            // as it won't get called in side effect B. If we do not do this, the archived cards
            // view will have infinite skeleton showing.
            if (!response?.data?.length) {
                setHasFetchedCards(true);
            }
        })();
    }, [loadArchivedCards, setHasFetchedCards]);

    // Side-Effect [B].
    // load the brands for each archived card and transform it before saving to redux
    useEffect(() => {
        if (!archivedCards.length) {
            return;
        }

        const neededBrandIds = archivedCards.map<number>((card) => card.brandId);

        const getArchivedCardsBrands = async () => {
            const response = await brandLoader.loadBrands(neededBrandIds);
            const archivedCardBrands = response.reduce<ECommBrand[]>((aggregateBrands, item) => {
                return item.error ? aggregateBrands : aggregateBrands.concat(item.data);
            }, []);
            dispatch(upsertBrands(keyBy(archivedCardBrands, (brand) => brand.id)));
            setHasFetchedCards(true);
        };

        getArchivedCardsBrands();
    }, [archivedCards, dispatch, setHasFetchedCards]);

    return {
        archivedCards,
        isLoading,
        refresh: loadArchivedCards,
    };
};
